import { useRef, useState, type ReactNode } from 'react'
import { Dropdown, type DropdownItem } from './Dropdown'
import { OverflowTip } from './Hover/OverflowTip'
import { Pressable } from './Pressable'
import { labelText } from './labelText'

export type CrumbSpec = {
  key: string
  /** Text/JSX shown as the crumb button. Pass `null` (or `undefined`) to
   *  render the chevron alone — used for leading "switcher" crumbs where the
   *  arrow is the whole affordance. */
  label: ReactNode
  onLabelClick?: () => void
  children?: DropdownItem[]
  ariaLabel?: string
  tourId?: string
}

interface Props {
  crumbs: CrumbSpec[]
  className?: string
  ariaLabel?: string
}

const CrumbChev = ({
  items,
  ariaLabel,
  tourId,
}: {
  items: DropdownItem[]
  ariaLabel: string
  tourId?: string
}) => {
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  return (
    <span className="dsBc__chevHost dsBc__chevHost--children">
      <Pressable
        ref={anchorRef}
        className={`dsBc__chev${open ? ' is-open' : ''}`}
        aria-label={ariaLabel}
        aria-expanded={open}
        data-tour-id={tourId}
        onClick={(event) => {
          event.stopPropagation()
          setOpen((current) => !current)
        }}
      >
        <span className="dsBc__chevIcon" aria-hidden="true">›</span>
      </Pressable>
      <Dropdown
        items={items}
        isOpen={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        ariaLabel={ariaLabel}
      />
    </span>
  )
}

/**
 * Locked breadcrumb — skin D "Underline trail".
 * Each crumb shows one trailing chevron that opens its CHILDREN dropdown.
 */
export const Breadcrumb = ({ crumbs, className, ariaLabel = 'Breadcrumb' }: Props) => (
  <nav className={`dsBc ${className ?? ''}`.trim()} aria-label={ariaLabel}>
    {crumbs.map((crumb) => (
      <span key={crumb.key} className="dsBc__crumb">
        {crumb.label != null && (
          <OverflowTip label={labelText(crumb.label)}>
            <Pressable
              className="dsBc__label"
              onClick={crumb.onLabelClick}
              disabled={!crumb.onLabelClick}
            >
              {crumb.label}
            </Pressable>
          </OverflowTip>
        )}
        {crumb.children && crumb.children.length > 0 && (
          <CrumbChev
            items={crumb.children}
            ariaLabel={`Jump into ${crumb.ariaLabel ?? 'crumb'} children`}
            tourId={crumb.tourId}
          />
        )}
      </span>
    ))}
  </nav>
)
